import { useMutation, useQuery } from "convex/react";
import { useEffect, useMemo, useState } from "react";

import type { AdministrationPageId } from "@reminder-tracker/domain";
import type {
  OperatorSupportCase,
  MaskedSupportQueueRow,
  SupportQueueFilters,
  SupportQueueSort,
  SupportQueueView,
} from "@reminder-tracker/backend-contracts";
import {
  supportCaseCategories,
  supportCaseSeverities,
  supportCaseStates,
} from "@reminder-tracker/domain";
import {
  claimSupportCase,
  listSupportQueue,
  listEligibleSupportOperators,
  openAssignedSupportCase,
  reassignOrReleaseSupportCase,
  syncOwnSupportEligibility,
  transitionAssignedSupportCase,
  triageAssignedSupportCase,
} from "../../backend/functions";
import {
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationStatusSummary,
  AdministrationAuditDetails,
} from "../../ui/administration-surface";

function reference(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
}

export function SupportCasesPanel({
  page,
  canLead = false,
  canWorkCases = true,
}: Readonly<{
  page: AdministrationPageId;
  canLead?: boolean;
  canWorkCases?: boolean;
}>): React.JSX.Element {
  const [view, setView] = useState<SupportQueueView>(
    page === "assigned-to-me" ? "assigned-to-me" : "needs-attention",
  );
  const [sort, setSort] = useState<SupportQueueSort>("target");
  const [category, setCategory] = useState<SupportQueueFilters["category"]>(null);
  const [severityFilter, setSeverityFilter] = useState<SupportQueueFilters["severity"]>(null);
  const [stateFilter, setStateFilter] = useState<SupportQueueFilters["state"]>(null);
  const [searchDraft, setSearchDraft] = useState("");
  const [exactCaseCode, setExactCaseCode] = useState<string | null>(null);
  useEffect(() => {
    setView(page === "assigned-to-me" ? "assigned-to-me" : "needs-attention");
    setExactCaseCode(null);
  }, [page]);
  const filters = useMemo<SupportQueueFilters | null>(() => {
    if (category === null && severityFilter === null && stateFilter === null) return null;
    return {
      state: stateFilter,
      category,
      severity: severityFilter,
      assignment: null,
      targetState: null,
      receivedFromEpochMilliseconds: null,
      receivedToEpochMilliseconds: null,
      updatedFromEpochMilliseconds: null,
      updatedToEpochMilliseconds: null,
    };
  }, [category, severityFilter, stateFilter]);
  const correlationId = useMemo(
    () => reference("corr_support_queue"),
    [view, sort, filters, exactCaseCode],
  );
  const queue = useQuery(listSupportQueue, {
    correlationId,
    view,
    sort,
    filters,
    exactCaseCode,
    paginationOpts: { cursor: null, numItems: 25 },
  });
  const claim = useMutation(claimSupportCase);
  const syncEligibility = useMutation(syncOwnSupportEligibility);
  const reassignOrRelease = useMutation(reassignOrReleaseSupportCase);
  const open = useMutation(openAssignedSupportCase);
  const triage = useMutation(triageAssignedSupportCase);
  const transition = useMutation(transitionAssignedSupportCase);
  const [selected, setSelected] = useState<OperatorSupportCase | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [response, setResponse] = useState("");
  const [severity, setSeverity] = useState<OperatorSupportCase["severity"]>("S4");
  const [handoverTarget, setHandoverTarget] = useState("");
  const [handoverReason, setHandoverReason] = useState<
    "capacity-handover" | "specialist-needed" | "operator-unavailable" | "incorrect-assignment"
  >("capacity-handover");
  const eligibleCorrelationId = useMemo(() => reference("corr_support_eligible"), []);
  const eligibleOperators = useQuery(
    listEligibleSupportOperators,
    canLead && selected !== null ? { correlationId: eligibleCorrelationId } : "skip",
  );
  const visibleTargetCounts = useMemo(() => {
    const items = queue?.items ?? [];
    return {
      missed: items.filter((item) => item.targetState === "missed").length,
      approaching: items.filter((item) => item.targetState === "approaching").length,
      onTrack: items.filter((item) => item.targetState === "on-track").length,
    };
  }, [queue]);

  useEffect(() => {
    if (!canWorkCases) return;
    const refresh = () => {
      void syncEligibility({ correlationId: reference("corr_support_eligibility") }).catch(
        () => undefined,
      );
    };
    refresh();
    const interval = globalThis.setInterval(refresh, 10 * 60_000);
    return () => globalThis.clearInterval(interval);
  }, [canWorkCases, syncEligibility]);

  const openCase = async (row: NonNullable<typeof queue>["items"][number]): Promise<void> => {
    setPendingCode(row.caseCode);
    setFeedback(null);
    try {
      if (row.assignmentState === "unassigned")
        await claim({
          correlationId: reference("corr_support_claim"),
          requestKey: reference("cmd_support_claim"),
          caseCode: row.caseCode,
          expectedAssignmentRevision: row.assignmentRevision,
          expectedWorkflowRevision: row.workflowRevision,
        });
      const detail = await open({
        correlationId: reference("corr_support_open"),
        requestKey: reference("cmd_support_open"),
        caseCode: row.caseCode,
      });
      setSelected(detail);
      setSeverity(detail.severity);
    } catch {
      setSelected(null);
      setFeedback(
        "This case changed or is no longer assigned to you. Refresh the queue and try again.",
      );
    } finally {
      setPendingCode(null);
    }
  };

  const refreshSelected = async (caseCode: string): Promise<void> => {
    const refreshed = await open({
      correlationId: reference("corr_support_refresh"),
      requestKey: reference("cmd_support_refresh"),
      caseCode,
    });
    setSelected(refreshed);
  };

  const saveTriage = async (): Promise<void> => {
    if (selected === null || pendingCode !== null) return;
    setPendingCode(selected.caseCode);
    setFeedback(null);
    try {
      await triage({
        correlationId: reference("corr_support_triage"),
        requestKey: reference("cmd_support_triage"),
        caseCode: selected.caseCode,
        category: selected.category,
        severity,
        reason: selected.severityConfirmed ? "new-evidence" : "intake-assessment",
        expectedWorkflowRevision: selected.workflowRevision,
        expectedAssignmentRevision: selected.assignmentRevision,
      });
      await refreshSelected(selected.caseCode);
      setFeedback("Triage is saved with its original review aim preserved.");
    } catch {
      setFeedback("This case changed or triage could not be saved. Refresh and review it again.");
    } finally {
      setPendingCode(null);
    }
  };

  const sendVisibleResponse = async (next: "ask-requester" | "resolve"): Promise<void> => {
    if (selected === null || pendingCode !== null) return;
    setPendingCode(selected.caseCode);
    setFeedback(null);
    try {
      await transition({
        correlationId: reference("corr_support_transition"),
        requestKey: reference("cmd_support_transition"),
        caseCode: selected.caseCode,
        transition: next,
        message: response,
        expectedWorkflowRevision: selected.workflowRevision,
      });
      setResponse("");
      await refreshSelected(selected.caseCode);
      setFeedback(
        next === "resolve"
          ? "The visible resolution is saved. The customer can confirm or reply during the review window."
          : "Your question is saved visibly in the private conversation.",
      );
    } catch {
      setFeedback("The case changed or your response could not be saved. Your text is still here.");
    } finally {
      setPendingCode(null);
    }
  };

  const handover = async (release: boolean): Promise<void> => {
    if (selected === null || pendingCode !== null || (!release && handoverTarget === "")) return;
    setPendingCode(selected.caseCode);
    setFeedback(null);
    try {
      await reassignOrRelease({
        correlationId: reference("corr_support_handover"),
        requestKey: reference("cmd_support_handover"),
        caseCode: selected.caseCode,
        targetOperatorReference: release ? null : handoverTarget,
        reason: handoverReason,
        expectedAssignmentRevision: selected.assignmentRevision,
      });
      setSelected(null);
      setHandoverTarget("");
      setFeedback(
        release
          ? "The case is safely back in the unassigned queue. Your private access has ended."
          : "The case is reassigned. Your private access has ended.",
      );
    } catch {
      setFeedback("The assignment changed or the selected operator is unavailable. Refresh first.");
    } finally {
      setPendingCode(null);
    }
  };

  return (
    <section className="support-workspace">
      <AdministrationPageHeader
        title={view === "assigned-to-me" ? "Assigned to me" : "Support work queue"}
        purpose="Start with cases closest to their response target. Queue rows stay masked until an authorised caseworker opens a case."
      />
      {selected === null && queue !== undefined ? (
        <>
          <AdministrationStatusSummary
            items={[
              {
                label: "Needs action",
                tone: "action",
                value: visibleTargetCounts.missed,
                title: "Missed response aims",
                description: "Open these visible cases first.",
              },
              {
                label: "Monitor",
                tone: "monitor",
                value: visibleTargetCounts.approaching,
                title: "Approaching response aims",
                description: "Keep these visible cases moving.",
              },
              {
                label: "Healthy",
                tone: "healthy",
                value: visibleTargetCounts.onTrack,
                title: "On track",
                description:
                  "No response-target intervention is indicated for these visible cases.",
              },
            ]}
          />
          <AdministrationImpactPanel
            label="Customer impact"
            title={
              visibleTargetCounts.missed > 0
                ? `${visibleTargetCounts.missed} visible ${visibleTargetCounts.missed === 1 ? "case has missed its" : "cases have missed their"} response aim`
                : visibleTargetCounts.approaching > 0
                  ? `${visibleTargetCounts.approaching} visible ${visibleTargetCounts.approaching === 1 ? "case is approaching its" : "cases are approaching their"} response aim`
                  : "No response-target risk is visible on this page"
            }
            description={
              visibleTargetCounts.missed > 0
                ? "Review missed cases before approaching and on-track work."
                : visibleTargetCounts.approaching > 0
                  ? "Review approaching cases before they miss their response aim."
                  : "Continue with the current queue order or choose another view."
            }
            action={
              visibleTargetCounts.missed > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    setView("needs-attention");
                    setStateFilter(null);
                    setExactCaseCode(null);
                  }}
                >
                  Review needs-attention queue
                </button>
              ) : visibleTargetCounts.approaching > 0 ? (
                <a className="secondary-action" href="#support-work-register">
                  Review approaching cases
                </a>
              ) : undefined
            }
            evidence={
              <p>
                Counts describe only the masked cases returned on this loaded page. No customer
                message content is used in this summary.
              </p>
            }
            tone={
              visibleTargetCounts.missed > 0
                ? "action"
                : visibleTargetCounts.approaching > 0
                  ? "monitor"
                  : "healthy"
            }
          />
        </>
      ) : null}
      {feedback === null ? null : (
        <p role="alert" className="support-feedback">
          {feedback}
        </p>
      )}
      {selected === null ? (
        <div className="support-queue" id="support-work-register" aria-busy={queue === undefined}>
          {page === "support-cases" ? (
            <>
              <nav className="support-quick-views" aria-label="Support queue views">
                {(
                  [
                    ["needs-attention", "Needs attention"],
                    ["unassigned", "Unassigned"],
                    ["waiting-for-you", "Waiting for customer"],
                    ["resolved", "Resolved"],
                    ["closed", "Closed"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    aria-pressed={exactCaseCode === null && view === value}
                    key={value}
                    onClick={() => {
                      setView(value);
                      setExactCaseCode(null);
                    }}
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </nav>
              <details className="inline-details support-queue-controls">
                <summary>Sort, filter or find an exact case</summary>
                <label className="support-sort" htmlFor="support-queue-sort">
                  Sort cases
                  <select
                    id="support-queue-sort"
                    onChange={(event) => setSort(event.target.value as SupportQueueSort)}
                    value={sort}
                  >
                    <option value="target">Review aim</option>
                    <option value="received-newest">Received — newest</option>
                    <option value="received-oldest">Received — oldest</option>
                    <option value="updated-newest">Updated — newest</option>
                    <option value="updated-oldest">Updated — oldest</option>
                    <option value="severity">Severity</option>
                  </select>
                </label>
                <div className="support-filters" aria-label="Filter support cases">
                  <label htmlFor="support-category-filter">
                    Category
                    <select
                      id="support-category-filter"
                      onChange={(event) =>
                        setCategory(
                          event.target.value === ""
                            ? null
                            : (event.target.value as SupportQueueFilters["category"]),
                        )
                      }
                      value={category ?? ""}
                    >
                      <option value="">All categories</option>
                      {supportCaseCategories.map((value) => (
                        <option key={value} value={value}>
                          {value.replaceAll("-", " ")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label htmlFor="support-severity-filter">
                    Severity
                    <select
                      id="support-severity-filter"
                      onChange={(event) =>
                        setSeverityFilter(
                          event.target.value === ""
                            ? null
                            : (event.target.value as SupportQueueFilters["severity"]),
                        )
                      }
                      value={severityFilter ?? ""}
                    >
                      <option value="">All severities</option>
                      {supportCaseSeverities.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label htmlFor="support-state-filter">
                    State
                    <select
                      id="support-state-filter"
                      onChange={(event) =>
                        setStateFilter(
                          event.target.value === ""
                            ? null
                            : (event.target.value as SupportQueueFilters["state"]),
                        )
                      }
                      value={stateFilter ?? ""}
                    >
                      <option value="">All states</option>
                      {supportCaseStates.map((value) => (
                        <option key={value} value={value}>
                          {value.replaceAll("-", " ")}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <form
                  className="support-code-search"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const normalized = searchDraft.trim().toUpperCase();
                    if (!/^RSC-[0-9A-HJKMNP-TV-Z]{16}$/u.test(normalized)) {
                      setFeedback("Enter the complete support reference, including RSC-.");
                      return;
                    }
                    setFeedback(null);
                    setExactCaseCode(normalized);
                  }}
                >
                  <label htmlFor="support-code-search">Find an exact support reference</label>
                  <input
                    id="support-code-search"
                    onChange={(event) => setSearchDraft(event.target.value)}
                    placeholder="RSC-…"
                    value={searchDraft}
                  />
                  <button className="secondary-action" type="submit">
                    Find case
                  </button>
                </form>
              </details>
            </>
          ) : null}
          {queue === undefined ? <p role="status">Preparing the masked support queue…</p> : null}
          {queue?.items.length === 0 ? (
            <p className="empty-state">No cases need attention in this view.</p>
          ) : null}
          {queue?.items.map((row) => (
            <article className="support-row" key={row.caseCode}>
              <div>
                <strong>{row.caseCode}</strong>
                <span>{row.category.replaceAll("-", " ")}</span>
              </div>
              <dl>
                <div>
                  <dt>State</dt>
                  <dd>{row.state.replaceAll("-", " ")}</dd>
                </div>
                <div>
                  <dt>Review aim</dt>
                  <dd>{supportTargetLabel(row.targetState)}</dd>
                </div>
                <div>
                  <dt>Assignment</dt>
                  <dd>{row.assignmentState.replaceAll("-", " ")}</dd>
                </div>
              </dl>
              <button
                className={
                  row.assignmentState === "unassigned" ? "primary-action" : "secondary-action"
                }
                disabled={pendingCode !== null || row.assignmentState === "assigned"}
                onClick={() => void openCase(row)}
                type="button"
              >
                {pendingCode === row.caseCode
                  ? "Checking access…"
                  : row.assignmentState === "unassigned"
                    ? "Claim and open"
                    : row.assignmentState === "assigned-to-you"
                      ? "Open case"
                      : "Assigned"}
              </button>
            </article>
          ))}
        </div>
      ) : (
        <article className="support-thread">
          <button className="secondary-action" onClick={() => setSelected(null)} type="button">
            Back to masked queue
          </button>
          <p className="eyebrow">{selected.caseCode}</p>
          <h2>{selected.subject}</h2>
          <p>
            {selected.category.replaceAll("-", " ")} · {selected.state.replaceAll("-", " ")}
          </p>
          <ol aria-label="Case conversation">
            {selected.messages.map((message) => (
              <li key={message.sequence}>
                <strong>{message.sender === "requester" ? "Customer" : "Support"}</strong>
                <p>{message.body}</p>
              </li>
            ))}
          </ol>
          <fieldset className="support-triage decision-stage">
            <legend>Review priority</legend>
            <label htmlFor="support-severity">Severity</label>
            <select
              id="support-severity"
              onChange={(event) =>
                setSeverity(event.target.value as OperatorSupportCase["severity"])
              }
              value={severity}
            >
              <option value="S1">S1 — urgent security, privacy or core outage</option>
              <option value="S2">S2 — important function blocked</option>
              <option value="S3">S3 — material problem with a workaround</option>
              <option value="S4">S4 — guidance or feedback</option>
            </select>
            <button
              className="secondary-action"
              disabled={pendingCode !== null}
              onClick={() => void saveTriage()}
              type="button"
            >
              {selected.severityConfirmed ? "Update triage" : "Confirm triage"}
            </button>
          </fieldset>
          {canLead ? (
            <AdministrationAuditDetails summary="Lead handover and assignment controls">
              <fieldset className="support-triage">
                <legend>Lead handover</legend>
                <label htmlFor="support-handover-target">Eligible operator</label>
                <select
                  id="support-handover-target"
                  onChange={(event) => setHandoverTarget(event.target.value)}
                  value={handoverTarget}
                >
                  <option value="">Choose an operator</option>
                  {eligibleOperators
                    ?.filter((operator) => !operator.currentOperator)
                    .map((operator) => (
                      <option key={operator.operatorReference} value={operator.operatorReference}>
                        {operator.displayLabel}
                      </option>
                    ))}
                </select>
                <label htmlFor="support-handover-reason">Reason</label>
                <select
                  id="support-handover-reason"
                  onChange={(event) =>
                    setHandoverReason(event.target.value as typeof handoverReason)
                  }
                  value={handoverReason}
                >
                  <option value="capacity-handover">Capacity handover</option>
                  <option value="specialist-needed">Specialist needed</option>
                  <option value="operator-unavailable">Operator unavailable</option>
                  <option value="incorrect-assignment">Incorrect assignment</option>
                </select>
                <div className="support-response-actions">
                  <button
                    className="primary-action"
                    disabled={pendingCode !== null || handoverTarget === ""}
                    onClick={() => void handover(false)}
                    type="button"
                  >
                    Reassign case
                  </button>
                  <button
                    className="caution-action"
                    disabled={pendingCode !== null}
                    onClick={() => void handover(true)}
                    type="button"
                  >
                    Release to queue
                  </button>
                </div>
              </fieldset>
            </AdministrationAuditDetails>
          ) : null}
          <div className="support-response">
            <h3>Respond to the customer</h3>
            <p>This message is visible to the customer. Review it before choosing the outcome.</p>
            <label htmlFor="support-response">Customer-visible message</label>
            <textarea
              id="support-response"
              maxLength={2000}
              onChange={(event) => setResponse(event.target.value)}
              rows={6}
              value={response}
            />
            <div className="support-response-actions">
              <button
                className="secondary-action"
                disabled={pendingCode !== null || response.trim().length === 0}
                onClick={() => void sendVisibleResponse("ask-requester")}
                type="button"
              >
                Ask customer
              </button>
              <button
                className="primary-action"
                disabled={pendingCode !== null || response.trim().length === 0}
                onClick={() => void sendVisibleResponse("resolve")}
                type="button"
              >
                Send resolution
              </button>
            </div>
          </div>
          <p className="access-evidence">Private access was recorded for this opening.</p>
        </article>
      )}
    </section>
  );
}

function supportTargetLabel(target: MaskedSupportQueueRow["targetState"]): string {
  if (target === "missed") return "Response aim missed — review first";
  if (target === "approaching") return "Response aim approaching";
  return "Response aim on track";
}
